import React, { useEffect, useState } from 'react';
import './ExtraInfo.css';
import { applyPolishTypography, parseSpecialMarkup } from '../utils/typography';

export default function ExtraInfo({ language = 'en' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = process.env.PUBLIC_URL + '/descriptions/99-eXtraInfo.json?t=' + Date.now();
    fetch(url, { cache: 'no-store' })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load eXtra Info');
        return res.json();
      })
      .then(data => {
        setItems(Array.isArray(data) ? data : [data]);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{color: 'red'}}>Error: {error}</div>;

  // Group items by title and subtitle, and support location1/description1, location2/description2, ...
  const grouped = [];
  items.forEach(item => {
    const title = item.title && item.title[language];
    // Support subtitle0 as first subtitle if present, else fallback to subtitle
    const subtitle = (item.subtitle0 && item.subtitle0[language]) || (item.subtitle && item.subtitle[language]);
    // Find all locationX/descriptionX pairs
    const cards = [];
    // Add location0/description0 as first card if present
    if (item.location0 && item.description0) {
      let url = item.url0 && item.url0[language];
      if (!url && item.url && item.url[language]) {
        url = item.url[language];
      }
      cards.push({
        location: item.location0[language],
        description: item.description0, // pass the whole description0 object for multi-paragraph rendering
        url
      });
    }
    let i = 1;
    while (item[`location${i}`] && item[`description${i}`]) {
      // Support url1, url2, ...
      let url = item[`url${i}`] && item[`url${i}`][language];
      // Fallback to section-level url if present and only one card
      if (!url && i === 1 && item.url && item.url[language]) {
        url = item.url[language];
      }
      cards.push({
        location: item[`location${i}`][language],
        description: item[`description${i}`][language],
        url
      });
      i++;
    }
    // Fallback for legacy location/description
    if (item.location && item.description) {
      cards.push({
        location: item.location[language],
        description: item.description[language]
      });
    } else if (item.description) {
      // If only description (no location), show as a card
      cards.push({
        location: null,
        description: item.description[language]
      });
    }
    grouped.push({ title, subtitle, cards });
  });

  return (
    <section className="extra-info-section" id="extra-info">
      {grouped.map((group, gidx) => (
        <div className="extra-info-section-group" key={gidx}>
          {group.title && (
            <div className="extra-info-title">{group.title}</div>
          )}
          {group.subtitle && (
            <div className="extra-info-subtitle">{group.subtitle}</div>
          )}
          <div className="extra-info-cards">
            {group.cards.map((card, idx) => {
              const cardUrl = card.url || card.link;
              // Make the very first card in the very first group wide
              const isWideCard = gidx === 0 && idx === 0;
              return (
                <div className={`extra-info-card${isWideCard ? ' extra-info-card-wide' : ''}`} key={idx}>
                  {card.location && (
                    <div className="extra-info-card-title">{card.location}</div>
                  )}
                  {card.title && <div className="extra-info-card-subtitle">{card.title}</div>}
                  {card.description && (
                    <div className="extra-info-card-text">
                      {typeof card.description === 'object' && card.description !== null
                        ? Object.keys(card.description)
                            .filter(k => k.startsWith(language))
                            .sort((a, b) => {
                              // Sort by number after language (e.g., en1, en2, en3b, en4i, en5bi)
                              const getNum = x => parseInt(x.match(/\d+/)?.[0] || '0', 10);
                              return getNum(a) - getNum(b);
                            })
                            .map(key => {
                              const val = card.description[key];
                              const isBold = key.endsWith('b') || key.endsWith('bi');
                              const isItalic = key.endsWith('i') || key.endsWith('bi');
                              // Parse [red], [b], [red][b] markup in the string
                              // Apply Polish typographic rules (no hanging letters/units)
                              const applyPolishTypography = (text) => {
                                if (typeof text !== 'string') return text;
                                // 1. Single-letter words (conjunctions/prepositions)
                                text = text.replace(/(?<=\s|^)([zaoíuwnadopzwe]) (?=\S)/gi, '$1&nbsp;');
                                // 2. Two-letter prepositions (na, do, od, po, za, we, etc.)
                                text = text.replace(/(?<=\s|^)(na|do|od|po|za|we|ze|ku|dla|przy|pod|nad|bez|czy|jak|lub|ani|ale) (?=\S)/gi, '$1&nbsp;');
                                // 3. Numbers + units
                                text = text.replace(/(\d+) (km|m|cm|mm|g|kg|ml|l|r\.|godz\.|min|s|st\.|zł|%)\b/gi, '$1&nbsp;$2');
                                // 4. Numbers + months (daty)
                                text = text.replace(/(\d+) (stycznia|lutego|marca|kwietnia|maja|czerwca|lipca|sierpnia|września|października|listopada|grudnia)/gi, '$1&nbsp;$2');
                                return text;
                              };
                              const parseSpecialMarkup = (text, lang) => {
                                if (typeof text !== 'string') return text;
                                let out = text;
                                // [red][b]...[/b][/red]
                                out = out.replace(/\[red\]\[b\](.*?)\[\/b\]\[\/red\]/g, '<span class="extra-info-red"><strong>$1</strong></span>');
                                out = out.replace(/\[blue\]\[b\](.*?)\[\/b\]\[\/blue\]/g, '<span class="extra-info-blue"><strong>$1</strong></span>');
                                out = out.replace(/\[green\]\[b\](.*?)\[\/b\]\[\/green\]/g, '<span class="extra-info-green"><strong>$1</strong></span>');
                                out = out.replace(/\[yellow\]\[b\](.*?)\[\/b\]\[\/yellow\]/g, '<span class="extra-info-yellow"><strong>$1</strong></span>');
                                out = out.replace(/\[orange\]\[b\](.*?)\[\/b\]\[\/orange\]/g, '<span class="extra-info-orange"><strong>$1</strong></span>');
                                // [b][i]...[/i][/b] and [i][b]...[/b][/i]
                                out = out.replace(/\[b\]\[i\](.*?)\[\/i\]\[\/b\]/g, '<strong><em>$1</em></strong>');
                                out = out.replace(/\[i\]\[b\](.*?)\[\/b\]\[\/i\]/g, '<em><strong>$1</strong></em>');
                                // [i]...[/i]
                                out = out.replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>');
                                // [red]...[/red] etc.
                                out = out.replace(/\[red\](.*?)\[\/red\]/g, '<span class="extra-info-red">$1</span>');
                                out = out.replace(/\[blue\](.*?)\[\/blue\]/g, '<span class="extra-info-blue">$1</span>');
                                out = out.replace(/\[green\](.*?)\[\/green\]/g, '<span class="extra-info-green">$1</span>');
                                out = out.replace(/\[yellow\](.*?)\[\/yellow\]/g, '<span class="extra-info-yellow">$1</span>');
                                out = out.replace(/\[orange\](.*?)\[\/orange\]/g, '<span class="extra-info-orange">$1</span>');
                                // [b]...[/b]
                                out = out.replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>');
                                return out;
                              };

                              // Detect language from key (pl = Polish)
                              const isPolish = key.startsWith('pl');
                              let displayVal = val;
                              if (isPolish) {
                                displayVal = applyPolishTypography(displayVal);
                              }
                              if (isBold && isItalic) {
                                return <p key={key} dangerouslySetInnerHTML={{__html: `<strong><em>${parseSpecialMarkup(displayVal)}</em></strong>`}} />;
                              } else if (isBold) {
                                return <p key={key} dangerouslySetInnerHTML={{__html: `<strong>${parseSpecialMarkup(displayVal)}</strong>`}} />;
                              } else if (isItalic) {
                                return <p key={key} dangerouslySetInnerHTML={{__html: `<em>${parseSpecialMarkup(displayVal)}</em>`}} />;
                              } else {
                                return <p key={key} dangerouslySetInnerHTML={{__html: parseSpecialMarkup(displayVal)}} />;
                              }
                            })
                        : (() => {
                            // Fallback for legacy/unnumbered or lower cards
                            const desc = card.description;
                            const isPolish = language === 'pl';
                            const formatted = isPolish ? applyPolishTypography(desc) : desc;
                            return <p dangerouslySetInnerHTML={{ __html: parseSpecialMarkup(formatted) }} />;
                          })()
                        }
                    </div>
                  )}
                  {cardUrl && (
                    <div className="extra-info-card-bottom">
                      <div className="extra-info-source">
                        <br></br>
                        Other source: <a
                          href={cardUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="extra-info-source-link"
                        >Wikipedia</a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
